/**
 * In-app navigation (D19).
 *
 * Every internal link in this app is a plain `<a href>`, which means the
 * browser was tearing the whole app down and rebuilding it on every tap:
 * download and parse the bundle, initialise Firebase, restore the auth
 * session, then re-run the Firestore query — a loading screen's worth of work
 * to move between two screens that share all of it.
 *
 * `navigateTo` swaps the URL without a document load, and `App` re-renders from
 * the new route. Anchors stay anchors: they keep their real `href`, so
 * middle-click, long-press, "open in new tab" and crawlers all behave, and a
 * cold hit on any URL still works because the router parses `location` on
 * boot. `interceptLinkClicks` upgrades the plain left-click in one place, so
 * no component has to know a router exists.
 */

/** Same event the browser fires on back/forward, so `App` needs one listener. */
function announce(): void {
  window.dispatchEvent(new PopStateEvent('popstate'));
}

export function navigateTo(to: string): void {
  const current = window.location.pathname + window.location.search;
  if (to === current) return;
  // Stamp the screen we left so Back can return to a run, a list, or
  // whatever opened this page — Home is a separate, explicit jump.
  window.history.pushState({ from: current }, '', to);
  // A new screen starts at the top; without this the browser keeps the
  // previous screen's scroll offset.
  window.scrollTo(0, 0);
  announce();
}

/**
 * The in-app screen that opened the current one, if we got here via
 * `navigateTo` / an intercepted link. A cold hit (WhatsApp, typed URL)
 * has nothing to return to inside X9.
 */
export function inAppBackTarget(): string | null {
  const from = window.history.state?.from;
  if (typeof from !== 'string' || !from.startsWith('/')) return null;
  return from;
}

/** Pop back to the screen that opened this one. No-op on a cold hit. */
export function goBackInApp(): boolean {
  if (!inAppBackTarget()) return false;
  window.history.back();
  return true;
}

/**
 * Upgrade left-clicks on same-origin links to in-app navigation.
 *
 * Returns a cleanup function. Deliberately conservative: anything that might
 * mean "leave this app" — another origin, a new tab, a download, a modified
 * click — is left to the browser.
 */
export function interceptLinkClicks(): () => void {
  const onClick = (event: MouseEvent) => {
    if (event.defaultPrevented) return;
    // Modified clicks are the user asking for a new tab or window.
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }

    const target = event.target as Element | null;
    const anchor = target?.closest?.('a');
    if (!anchor) return;

    const href = anchor.getAttribute('href');
    if (!href) return;
    if (anchor.hasAttribute('download') || anchor.hasAttribute('target')) return;
    // `rel="external"` is how a component opts out of in-app navigation.
    if (anchor.getAttribute('rel')?.includes('external')) return;

    let url: URL;
    try {
      url = new URL(href, window.location.href);
    } catch {
      return;
    }
    // Other origins, and schemes like `mailto:` or the native app handoff.
    if (url.origin !== window.location.origin) return;
    // In-page anchors are the browser's job.
    if (url.hash && url.pathname === window.location.pathname) return;

    event.preventDefault();
    navigateTo(url.pathname + url.search);
  };

  document.addEventListener('click', onClick);
  return () => document.removeEventListener('click', onClick);
}
